import React, { useEffect, useMemo, useState } from 'react';
import {
  getGTMClientId,
  AnalyticsPlatformProvider,
  useUser,
  resolvePersistedAnalyticsAttribution,
} from '@skin-seoul/service';
import { usePersonalizationTracker, BridgeApi } from '@skin-seoul/utils';
import { useCookieConsentStatus } from './ShortcodeCookieConsentProvider';

interface Props {
  children: React.ReactNode;
}

export function ShortcodeAnalyticsPlatformProvider({ children }: Props) {
  const consent = useCookieConsentStatus();
  const { user } = useUser();
  const tracker = usePersonalizationTracker();
  const [webViewConfig, setWebViewConfig] = useState(() => BridgeApi.getAnalyticsConfig());

  useEffect(
    () => BridgeApi.subscribeToAnalyticsConfigChange(() => setWebViewConfig(BridgeApi.getAnalyticsConfig())),
    [],
  );

  // WordPress does full page reloads on navigation (no SPA routing), so
  // window.location.* and document.referrer are always fresh on mount.
  const platformContext = useMemo(() => {
    const clientId = webViewConfig ? (tracker.getActorId() ?? '') : getGTMClientId();
    const isBrowser = typeof window !== 'undefined';
    const currentPageUrl = isBrowser ? window.location.href : undefined;
    const hasAdvertisingConsent = webViewConfig ? webViewConfig.adStorage === 'granted' : consent.hasAdvertisingConsent;
    const attribution = resolvePersistedAnalyticsAttribution(currentPageUrl, { hasAdvertisingConsent });

    return {
      platform: webViewConfig?.platform ?? ('Web' as const),
      clientId,
      userId: user?.userId,
      pagePath: currentPageUrl,
      pageTitle: isBrowser ? document.title : undefined,
      pageReferrer: isBrowser ? document.referrer || undefined : undefined,
      consent: {
        analyticsStorage: consent.hasAnalyticsConsent ? ('granted' as const) : ('denied' as const),
        adStorage: hasAdvertisingConsent ? ('granted' as const) : ('denied' as const),
        adUserData: hasAdvertisingConsent ? ('granted' as const) : ('denied' as const),
        adPersonalization: hasAdvertisingConsent ? ('granted' as const) : ('denied' as const),
      },
      osName: webViewConfig?.osName,
      deviceModel: webViewConfig?.deviceModel,
      appVersion: webViewConfig?.appVersion,
      ...attribution,
      debugMode: process.env.NODE_ENV === 'development',
    };
  }, [consent.hasAnalyticsConsent, consent.hasAdvertisingConsent, user?.userId, tracker, webViewConfig]);

  return <AnalyticsPlatformProvider value={platformContext}>{children}</AnalyticsPlatformProvider>;
}
